"use client";

import {PrimitiveIcon} from "@gluestack-ui/icon";
import {createInput} from "@gluestack-ui/input";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {withStates} from "@gluestack-ui/nativewind-utils/withStates";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {withStyleContextAndStates} from "@gluestack-ui/nativewind-utils/withStyleContextAndStates";
import {cssInterop} from "nativewind";
import type {ReactNode, Ref} from "react";
import React from "react";
import {Platform, Pressable, TextInput, View} from "react-native";

import {Box} from "../box";
import {Text} from "../text";

const SCOPE = "INPUT";

const InputWrapper = React.forwardRef<
    React.ComponentRef<typeof View>,
    React.ComponentProps<typeof View>
>(({...props}, ref) => {
    return <View {...props} ref={ref} />;
});

InputWrapper.displayName = "InputWrapper";

const UIInput = createInput({
    Root:
        Platform.OS === "web"
            ? withStyleContext(InputWrapper, SCOPE)
            : withStyleContextAndStates(InputWrapper, SCOPE),
    Icon: PrimitiveIcon,
    Slot: Pressable,
    Input: Platform.OS === "web" ? TextInput : withStates(TextInput)
});

const inputStyle = tva({
    base:
        "h-[36px]" +
        " bg-white" +
        " flex-row" +
        " items-center" +
        " rounded-sm" +
        " overflow-hidden" +
        " content-center" +
        " border-blueGray-200" +
        " data-[disabled=true]:opacity-40" +
        " data-[disabled=true]:bg-blueGray-100" +
        " data-[disabled=true]:border-blueGray-100",

    variants: {
        variant: {
            outline:
                "rounded-sm" +
                " border" +
                " data-[invalid=true]:border-error-600" +
                " data-[invalid=true]:hover:border-error-600" +
                " data-[focus=true]:web:ring-2" +
                " data-[focus=true]:web:ring-blueGray-200/[.5]" +
                " data-[focus=true]:native:border-2" +
                " data-[invalid=true]:web:ring-1" +
                " data-[invalid=true]:web:ring-error-600" +
                " data-[invalid=true]:data-[focus=true]:web:ring-2" +
                " data-[invalid=true]:data-[focus=true]:web:ring-error-600" +
                " data-[invalid=true]:data-[disabled=true]:web:ring-1" +
                " data-[invalid=true]:data-[disabled=true]:web:ring-error-600",
            underlined:
                "rounded-none border-b-2 border-trueGray-300" +
                " data-[focus=true]:border-primary-600" +
                " data-[invalid=true]:border-b-2" +
                " data-[invalid=true]:border-error-600" +
                " data-[invalid=true]:hover:border-error-600" +
                " data-[invalid=true]:data-[focus=true]:border-error-600" +
                " data-[invalid=true]:data-[focus=true]:hover:border-error-600" +
                " data-[invalid=true]:data-[disabled=true]:hover:border-error-600"
        },
        hasLeftElement: {
            true: "pl-3"
        },
        hasRightElement: {
            true: "pr-3"
        }
    }
});

const inputIconStyle = tva({
    base: "justify-center items-center h-4 w-4 mr-3 fill-blueGray-400 stroke-transparent"
});

const inputSlotStyle = tva({
    base: "justify-center items-center bg-white web:disabled:cursor-not-allowed"
});

const inputFieldStyle = tva({
    base:
        "min-h-[34px]" +
        " py-2" +
        " px-3" +
        " font-body" +
        " leading-sm" +
        " text-sm" +
        " font-normal" +
        " text-blueGray-800" +
        " bg-transparent!" +
        " flex-1" +
        " placeholder:text-blueGray-400" +
        " web:cursor-text" +
        " web:data-[disabled=true]:cursor-not-allowed",

    parentVariants: {
        variant: {
            underlined: "web:outline-0 web:outline-none px-0",
            outline: "web:outline-0 web:outline-none",
            rounded: "web:outline-0 web:outline-none px-4"
        },
        hasLeftElement: {
            true: "pl-2"
        },
        hasRightElement: {
            true: "pr-2"
        }
    }
});

const inputRightAddonStyle = tva({
    base:
        "h-full" +
        " justify-center items-center" +
        " bg-blueGray-100" +
        " border-l border-blueGray-200 border-solid" +
        " px-3",
    variants: {
        isDisabled: {
            true: "border-blueGray-300"
        }
    }
});

const inputLeftAddonStyle = tva({
    base:
        "h-full" +
        " justify-center items-center" +
        " bg-blueGray-100" +
        " border-r border-blueGray-200 border-solid" +
        " px-2",
    variants: {
        isDisabled: {
            true: "border-blueGray-300"
        }
    }
});

cssInterop(InputWrapper, {className: "style"});
cssInterop(UIInput.Slot, {className: "style"});
cssInterop(UIInput.Input, {
    className: {target: "style", nativeStyleToProp: {textAlign: true}}
});
cssInterop(UIInput.Icon, {
    className: {
        target: "style",
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: "classNameColor",
            stroke: true
        }
    }
});

type IInputSlotProps = React.ComponentProps<typeof UIInput.Slot> &
    VariantProps<typeof inputSlotStyle> & {className?: string};

const InputSlot = React.forwardRef<React.ElementRef<typeof UIInput.Slot>, IInputSlotProps>(
    ({className, ...props}, ref) => {
        return (
            <UIInput.Slot
                ref={ref}
                {...props}
                className={inputSlotStyle({
                    class: className
                })}
            />
        );
    }
);

export type IInputProps = React.ComponentProps<typeof UIInput> &
    Omit<VariantProps<typeof inputStyle>, "hasLeftElement" | "hasRightElement"> & {
        className?: string;
        leftElement?: ReactNode;
        rightElement?: ReactNode;
    };
const Input = React.forwardRef<InputRef, IInputProps>(
    (
        {className, variant = "outline", children, leftElement, rightElement, isDisabled, ...props},
        ref
    ) => {
        const hasLeftElement = !!leftElement;
        const hasRightElement = !!rightElement;

        return (
            <UIInput
                ref={ref}
                {...props}
                className={inputStyle({variant, class: className, hasLeftElement, hasRightElement})}
                context={{
                    variant,
                    hasLeftElement,
                    hasRightElement,
                    isDisabled
                }}
                isDisabled={isDisabled}
            >
                {typeof leftElement === "string" ? (
                    <Text color="blueGray.400" variant="sm">
                        {leftElement}
                    </Text>
                ) : (
                    leftElement
                )}
                {children}
                {typeof rightElement === "string" ? (
                    <Text color="blueGray.400" variant="sm">
                        {rightElement}
                    </Text>
                ) : (
                    rightElement
                )}
            </UIInput>
        );
    }
);

type IInputIconProps = React.ComponentProps<typeof UIInput.Icon> & {
    className?: string;
    as: React.ElementType;
};

const InputIcon = React.forwardRef<React.ElementRef<typeof UIInput.Icon>, IInputIconProps>(
    ({className, size, as: Component, ...props}, ref) => {
        return <Component {...props} ref={ref} className={inputIconStyle({class: className})} />;
    }
);

type UIInputInputProps = React.ComponentProps<typeof UIInput.Input>;
type BaseInputFieldProps = Omit<UIInputInputProps, "type" | "value"> &
    VariantProps<typeof inputFieldStyle> & {
        // support the value from web because we assume the default platform as native
        type?: UIInputInputProps["type"] | React.ComponentProps<"input">["type"];
        value?: string | number;
        // extra ones from web
        name?: string;
    };
type IInputFieldProps<T = never> = IsNever<T> extends true
    ? BaseInputFieldProps
    : React.ComponentProps<"input"> & {
          // extra ones from the native
          testID?: string;
          onChangeText?: (text: string) => void;
      };

export type InputRef<T = never> = IsNever<T> extends true
    ? React.ElementRef<typeof UIInput.Input>
    : HTMLInputElement;

export type InputFieldRef<T = never> = IsNever<T> extends true
    ? React.ElementRef<typeof UIInput.Input>
    : HTMLInputElement;

const InputField = React.forwardRef<InputFieldRef, IInputFieldProps>(
    ({className, type, value, name, onChange, onChangeText, ...props}, ref) => {
        const {variant: parentVariant, hasLeftElement, hasRightElement} = useStyleContext(SCOPE);
        // const innerRef = useRef<InputFieldRef<"web"> | InputFieldRef<"native">>(null);
        return (
            <UIInput.Input
                ref={ref}
                // @ts-expect-error
                type={type}
                // the thing is `register` won't pass `value` to input so we need to ensure we don't pass
                // the `value` into this component. But the `name` should present.
                {...(name ? {} : {value: value === undefined ? "" : value.toString()})}
                {...props}
                className={inputFieldStyle({
                    parentVariants: {
                        variant: parentVariant,
                        hasLeftElement,
                        hasRightElement
                    },
                    class: className
                })}
                onChange={e => {
                    // The `TextInput` of `react-native` won't accept `name`
                    // which is the reason why this won't work with `register` of `react-form-hook`
                    // so this would fix for the web, however, don't use `register` for universal component
                    if (process.env.IS_WEB && e.target instanceof HTMLInputElement) {
                        if (name) {
                            e.target.name = name;
                        }
                    }
                    onChange?.(e);
                }}
                onChangeText={onChangeText}
            />
        );
    }
) as {
    <T = never>(
        props: IInputFieldProps<T> & {ref?: Ref<InputFieldRef<T>>}
    ): React.ReactElement<any>;
    displayName: string;
};

type InputAddonProps = {
    testID?: string;
    className?: string;
    children?: React.ReactNode;
    onPress?: () => void;
};

const InputRightAddon: React.FC<InputAddonProps> = ({className, children, onPress, testID}) => {
    const {isDisabled} = useStyleContext(SCOPE);

    const node = typeof children === "string" ? <Text variant="sm">{children}</Text> : children;

    if (onPress) {
        return (
            <Pressable
                className={inputRightAddonStyle({className, isDisabled})}
                disabled={isDisabled}
                testID={testID}
                onPress={onPress}
            >
                {node}
            </Pressable>
        );
    }

    return (
        <Box className={inputRightAddonStyle({className, isDisabled})} testID={testID}>
            {node}
        </Box>
    );
};

const InputLeftAddon: React.FC<InputAddonProps> = ({className, children, onPress, testID}) => {
    const {isDisabled} = useStyleContext(SCOPE);

    const node = typeof children === "string" ? <Text variant="sm">{children}</Text> : children;

    if (onPress) {
        return (
            <Pressable
                className={inputLeftAddonStyle({className, isDisabled})}
                disabled={isDisabled}
                testID={testID}
                onPress={onPress}
            >
                {node}
            </Pressable>
        );
    }

    return (
        <Box className={inputLeftAddonStyle({className, isDisabled})} testID={testID}>
            {node}
        </Box>
    );
};

Input.displayName = "Input";
InputIcon.displayName = "InputIcon";
InputSlot.displayName = "InputSlot";
InputField.displayName = "InputField";

export {Input, InputField, InputIcon, InputSlot, InputRightAddon, InputLeftAddon};
